/**
 * Book 页面业务逻辑 Composable
 * 负责处理书籍详情、章节信息、阅读跳转等所有业务逻辑
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import type { BookPageConfig } from '../configs/types'
import { api } from '@/api'
import { $formatErrorMessage } from '@/helpers'
import { useToastStore, useUserStore } from '@/stores'
import { useGotoBack } from '@/composables'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { useBookExposure } from '@/composables/tracks/useBookExposure'
import { useGamePlayPoint } from '@/composables/tracks/useGamePlayPoint'
import { reportClarity } from '@/utils/tracking'
import { selectCoverUrl } from '@/utils/selectCoverUrl'
import { useAxonPageView } from '@/composables/axon/useAxonPageView'

async function hmacSHA256Base64Url(key: string, message: string): Promise<string> {
  const enc = new TextEncoder()
  const cryptoKey = await crypto.subtle.importKey(
    'raw', enc.encode(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false, ['sign'],
  )
  const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(message))
  return btoa(String.fromCharCode(...new Uint8Array(sig)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

async function buildProgressResetAuth(params: {
  antiForgeryToken: string
  antiForgeryTokenId: string
  contentId: string
  targetChapterId: string
  scope: string
  clearRoleConfig: boolean
}): Promise<{ requestId: string; timestamp: number; requestSignature: string }> {
  const requestId = `reset-${crypto.randomUUID()}`
  const timestamp = Math.floor(Date.now() / 1000)

  const signingString = [
    'if-write-sign-v2',
    'operation=progress/reset',
    `tokenId=${params.antiForgeryTokenId}`,
    `requestId=${requestId}`,
    `contentId=${params.contentId}`,
    `targetChapterId=${params.targetChapterId}`,
    `scope=${params.scope}`,
    `clearRoleConfig=${params.clearRoleConfig}`,
    `timestamp=${timestamp}`,
  ].join('\n')

  const requestSignature = await hmacSHA256Base64Url(params.antiForgeryToken, signingString)
  return { requestId, timestamp, requestSignature }
}

export function useBookPage(contentId: Ref<string>, config: BookPageConfig) {
  const router = useRouter()
  const { $toast } = useToastStore()
  const userStore = useUserStore()
  const { elementClick } = useElementClick()
  const { bookExposure } = useBookExposure()
  const { playTraceId, trackContentLoaded, trackStartGame, trackGameEngineLoaded } = useGamePlayPoint(contentId.value)
  const gotoBack = useGotoBack()
  // Axon page_view：进入落地页时上报（见下方按 contentId 监听）
  const { reportPageView } = useAxonPageView()

  const skeleton = ref(false)
  const book = ref<any>()
  const popupTOC = ref(false)
  const coverUrl = computed(() => {
    const urls = book.value?.loadingCoverUrls
    const memberId = userStore.memberId
    if (urls && urls.length > 0 && memberId)
      return selectCoverUrl(memberId, urls) || book.value?.coverUrl || ''

    return book.value?.coverUrl || ''
  })

  const antiForgeryToken = ref('')
  const antiForgeryTokenId = ref('')
  const currentProgressChapterId = ref('')
  const unlockedChapterIds = ref<string[]>([])
  const isResetting = ref(false)

  /**
   * 获取书籍详情
   */
  async function fetchBook() {
    if (!book.value)
      skeleton.value = true
    const { data } = await api.get<any, GetContentGetcontentdetailResponse>(
      '/interactive/book/detail',
      { params: { contentId: contentId.value } },
    )
    book.value = data
    skeleton.value = false

    bookExposure({
      book_id: data.contentId,
      book_name: data.contentName,
    })

    try {
      sessionStorage.setItem(`interactive-detail-${contentId.value}`, JSON.stringify(data))
    }
    catch (error) {
      console.error('Error getting cover URL:', error)
    }

    fetchProgress()
  }

  async function fetchProgress() {
    try {
      const { data } = await api.post<any, any>(
        '/interactive/progress/get',
        null,
        { params: { contentId: contentId.value } },
      )
      antiForgeryToken.value = data.antiForgeryToken ?? ''
      antiForgeryTokenId.value = data.antiForgeryTokenId ?? ''
      currentProgressChapterId.value = data.chapterId ?? ''
      unlockedChapterIds.value = data.unlockedChapterIds ?? []
    }
    catch {
      // 获取失败不阻断页面，重置时会提示
    }
  }

  async function onResetConfirm(
    chapterId: string | number | undefined,
    clearRoleConfig: boolean,
  ) {
    if (isResetting.value)
      return
    isResetting.value = true
    try {
      const scope = chapterId === undefined ? 'FULL_BOOK' : 'TARGET_CHAPTER'
      const targetChapterId = chapterId !== undefined ? String(chapterId) : ''

      const { requestId, timestamp, requestSignature } = await buildProgressResetAuth({
        antiForgeryToken: antiForgeryToken.value,
        antiForgeryTokenId: antiForgeryTokenId.value,
        contentId: contentId.value,
        targetChapterId,
        scope,
        clearRoleConfig,
      })

      const body: Record<string, any> = {
        contentId: contentId.value,
        scope,
        antiForgeryTokenId: antiForgeryTokenId.value,
        requestId,
        requestSignature,
        timestamp,
        clearRoleConfig,
      }
      if (targetChapterId)
        body.targetChapterId = targetChapterId

      const { data } = await api.post<any, any>('/interactive/progress/reset', body)

      antiForgeryToken.value = data.antiForgeryToken ?? ''
      antiForgeryTokenId.value = data.antiForgeryTokenId ?? ''
      currentProgressChapterId.value = data.chapterId ?? ''
      unlockedChapterIds.value = data.unlockedChapterIds ?? []

      $toast('Reset successful')
      fetchBook()
    }
    catch (err: any) {
      if (err?.code === 401 || err?.code === 10001)
        $toast('Please log in again')
      else
        $toast(err?.msg || 'Reset failed, please try again')
    }
    finally {
      isResetting.value = false
    }
  }

  async function onBookshelf() {
    reportClarity('落地页加入书架按钮点击')
    const params = {
      memberId: userStore.memberId,
      contentId: contentId.value,
      contentType: 6,
    }
    const { data } = await api.post<any>(
      '/bookshelf/add',
      params,
    )
    if (data)
      $toast(config.texts.bookshelfSuccess)
    else
      $toast($formatErrorMessage(config.texts.bookshelfError))
  }

  /**
   * 开始/继续阅读
   */
  async function onRead() {
    reportClarity('落地页阅读按钮点击')
    elementClick('read')
    trackStartGame()

    const currentParams = new URLSearchParams(window.location.search)
    const query: Record<string, string> = {}
    currentParams.forEach((value, key) => {
      query[key] = value
    })
    query.contentId = contentId.value
    query.playTraceId = playTraceId.value
    if (coverUrl.value) {
      query.coverUrl = coverUrl.value
      sessionStorage.setItem('gameCoverUrl', coverUrl.value)
    }
    // Axon view_item：内容名透传给 /game 页，供游戏画面加载完成时上报
    if (book.value?.contentName)
      query.contentName = book.value.contentName

    // Axon view_item：标记本次是从落地页 Reading 进入，game 页据此判断是否上报（一次性消费）
    sessionStorage.setItem('axonViewItemFromReading', '1')

    router.push({ name: 'game', query })
  }

  /**
   * 打开目录弹窗
   */
  function openPopupTOC() {
    reportClarity('落地页目录按钮点击')
    popupTOC.value = true
  }

  /**
   * 跳转到标签页
   */
  function gotoTags(tagId: string) {
    reportClarity('落地页标签按钮点击')
    router.push({ name: 'tags', query: { tagId } })
  }

  // ========== 页面停留时长埋点 ==========
  const bookPageStartTime = ref(0)

  function reportBookTime() {
    if (!window.clarity || !bookPageStartTime.value)
      return
    const duration = Math.round((performance.now() - bookPageStartTime.value) / 1000)
    window.clarity('set', '落地页停留时长', `${duration} 秒`)
  }

  /**
   * 上报页面停留时长(微软统计)
   * 进入页面记录开始时间，页面隐藏或离开时上报
   */
  function trackingBookTime() {
    bookPageStartTime.value = performance.now()

    function handleVisibilityChange() {
      if (document.hidden)
        reportBookTime()
      else
        bookPageStartTime.value = performance.now()
    }

    // pagehide 在关闭 tab/浏览器时比 beforeunload 更可靠（尤其移动端）
    function handlePageHide() {
      reportBookTime()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pagehide', handlePageHide)

    onUnmounted(() => {
      reportBookTime()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pagehide', handlePageHide)
    })
  }

  function onCoverLoaded() {
    trackContentLoaded()
  }

  function onPreloadComplete(costMs: number) {
    trackGameEngineLoaded(costMs)
  }

  // ========== 初始化和监听 ==========
  // getLeadSentence()
  fetchBook()
  trackingBookTime()

  watch(contentId, () => {
    // getLeadSentence()
    fetchBook()
  })

  // Axon page_view：进入落地页（含切换书籍）时上报一次；待用户信息就绪以带上 user_id
  watch(contentId, () => {
    userStore.init().then(reportPageView)
  }, { immediate: true })

  onMounted(() => {
    reportClarity('落地页页面曝光')
  })

  return {
    // 状态
    book,
    coverUrl,
    skeleton,
    popupTOC,
    currentProgressChapterId,
    unlockedChapterIds,
    isResetting,

    // 方法
    fetchBook,
    onBookshelf,
    onRead,
    openPopupTOC,
    gotoTags,
    gotoBack,
    onCoverLoaded,
    onPreloadComplete,
    onResetConfirm,
    playTraceId,
  }
}
